import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';

export interface FluidCapacityHourSlot {
  id: string;
  startMinute: number;
  durationMinutes: number;
  startLabel: string;
  endLabel: string;
  specialistAvailable: number;
  specialistTotal: number;
  deviceAvailable: number;
  deviceTotal: number;
  status?: 'available' | 'reserved' | 'blocked' | 'completed' | 'past';
  intelligence?: FluidCapacitySlotIntelligence;
}

export interface FluidCapacitySlotIntelligence {
  signal: 'healthy' | 'blocked' | 'expand' | 'hire-specialists' | 'add-devices' | 'preference-skew' | 'mix-waste';
  label: string;
  followUp: string;
  deviceRequiredTimePercent: number;
  specialistIdlePercent: number;
  deviceIdlePercent: number;
  preferenceSkewPercent?: number;
  businessSuccessScore?: number;
  effectiveThroughputScore?: number;
}

export interface FluidCapacityCarryState {
  overflowSlotId: string | null;
  overflowMinutes: number;
}

export type FluidCapacitySplitMode = 'fixed' | 'adaptive';
export type FluidCapacityGeometryMode = 'diagonal' | 'level';
export type FluidCapacityRenderVariant =
  | 'standard'
  | 'hybrid'
  | 'throughput'
  | 'pressure'
  | 'soft-ribbon'
  | 'soft-palette'
  | 'state-layer'
  | 'throughput-rail'
  | 'throughput-band'
  | 'bottleneck-scan'
  | 'continuity-rail'
  | 'medium-pressure'
  | 'focus-flow'
  | 'operational-candidate'
  | 'capacity-intelligence'
  | 'capacity-intelligence-compact'
  | 'capacity-intelligence-maturity';

type FluidCapacityResource = 'device' | 'specialist';
type FluidCapacityBottleneck = 'none' | 'device' | 'specialist' | 'both';
type FluidCapacityOperationalState = 'stable' | 'unstable' | 'overloaded' | 'recovery' | 'predicted-saturation';
type FluidCapacityRailState = 'healthy' | 'constrained' | 'fragile' | 'dead' | 'recovery';
type FluidCapacitySignalStrength = 'strong' | 'weak' | 'chronic';
type ResolvedFluidCapacitySlotIntelligence =
  Required<Omit<FluidCapacitySlotIntelligence, 'businessSuccessScore' | 'effectiveThroughputScore'>>
  & Pick<FluidCapacitySlotIntelligence, 'businessSuccessScore' | 'effectiveThroughputScore'>;

type RgbColor = readonly [number, number, number];

interface FluidCapacityVisualCarry {
  deviceExitColor: string;
  specialistExitColor: string;
  splitExitPercent: number;
  pressureExitRatio: number;
}

interface FluidCapacityHourSegment extends FluidCapacityHourSlot {
  leftPercent: number;
  widthPercent: number;
  clippedStartMinute: number;
  clippedEndMinute: number;
  startMinuteLabel: string;
  endMinuteLabel: string;
  resourceSummaryLabel: string;
  splitLeftPercent: number;
  splitRightPercent: number;
  splitLeftCss: string;
  splitRightCss: string;
  splitDataLeftPercent: number;
  splitDataRightPercent: number;
  deviceColorStart: string;
  deviceColorEnd: string;
  deviceFlowInletColor: string;
  specialistColorStart: string;
  specialistColorEnd: string;
  specialistFlowInletColor: string;
  continuesFromPrevious: boolean;
  continuesToNext: boolean;
  hasFlowCarry: boolean;
  deviceGradientId: string;
  deviceFlowGradientId: string;
  specialistGradientId: string;
  specialistFlowGradientId: string;
  devicePath: string;
  deviceFlowPath: string;
  specialistPath: string;
  specialistFlowPath: string;
  boundaryPath: string;
  deviceTargetBoundaryPath: string;
  specialistTargetBoundaryPath: string;
  bottleneckKind: FluidCapacityBottleneck;
  operationalState: FluidCapacityOperationalState;
  throughputAvailable: number;
  throughputTotal: number;
  throughputWidthCss: string;
  throughputOpacityCss: string;
  throughputColorCss: string;
  pressureOpacityCss: string;
  flowOpacityCss: string;
  businessSuccessRatio: number;
  showThroughput: boolean;
  showPressureOverlay: boolean;
  showStateLayer: boolean;
  isDeadThroughput: boolean;
  isBusinessSuccess: boolean;
  isNearBusinessSuccess: boolean;
  railState: FluidCapacityRailState;
  showCapacityIntelligence: boolean;
  intelligenceSignal: FluidCapacitySlotIntelligence['signal'] | 'none';
  intelligenceStrength: FluidCapacitySignalStrength;
  repeatsIntelligenceSignal: boolean;
  intelligenceLabel: string;
  intelligenceFollowUp: string;
  deviceRequiredTimeCss: string;
  specialistOnlyTimeCss: string;
  specialistIdleCss: string;
  deviceIdleCss: string;
  preferenceSkewCss: string;
}

const LOAD_COLOR_SCALE: Record<FluidCapacityResource, { healthyStart: RgbColor; healthyEnd: RgbColor }> = {
  device: {
    healthyStart: [187, 247, 208],
    healthyEnd: [34, 197, 94],
  },
  specialist: {
    healthyStart: [219, 234, 254],
    healthyEnd: [147, 197, 253],
  },
};

const LOAD_WARNING_START: RgbColor = [254, 243, 199];
const LOAD_WARNING_END: RgbColor = [251, 191, 36];
const LOAD_DANGER_START: RgbColor = [254, 202, 202];
const LOAD_DANGER_END: RgbColor = [239, 68, 68];
const SPLIT_VERTICAL_INSET_PERCENT = 10;
const SPLIT_RACCORD_WIDTH_PERCENT = 10;
const COLOR_FLOW_WIDTH_PERCENT = 2.6;

@Component({
  selector: 'lib-fluid-capacity-hour',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="fluid-capacity-hour"
      [class.fluid-capacity-hour--throughput-rail]="renderVariant === 'throughput-rail'"
      [class.fluid-capacity-hour--throughput-band]="renderVariant === 'throughput-band'"
      [class.fluid-capacity-hour--bottleneck-scan]="renderVariant === 'bottleneck-scan'"
      [class.fluid-capacity-hour--continuity-rail]="renderVariant === 'continuity-rail'"
      [class.fluid-capacity-hour--medium-pressure]="renderVariant === 'medium-pressure'"
      [class.fluid-capacity-hour--focus-flow]="renderVariant === 'focus-flow'"
      [class.fluid-capacity-hour--operational-candidate]="renderVariant === 'operational-candidate'"
      [class.fluid-capacity-hour--capacity-intelligence]="renderVariant === 'capacity-intelligence'"
      [class.fluid-capacity-hour--capacity-intelligence-compact]="renderVariant === 'capacity-intelligence-compact'"
      [class.fluid-capacity-hour--capacity-intelligence-maturity]="renderVariant === 'capacity-intelligence-maturity'"
      [style.--fluid-hour-min-height.px]="minHeight"
    >
      <div class="fluid-capacity-hour__label">{{ hourLabel }}</div>
      <div class="fluid-capacity-hour__track">
        @if (showHourThroughputRail) {
          <div class="fluid-capacity-hour__hour-throughput" aria-hidden="true">
            @for (slot of renderedSlots; track slot.id) {
              <span
                class="fluid-capacity-hour__hour-throughput-segment"
                [style.left.%]="slot.leftPercent"
                [style.width.%]="slot.widthPercent"
                [style.--fluid-throughput-width]="slot.throughputWidthCss"
                [style.--fluid-throughput-color]="slot.throughputColorCss"
                [style.--fluid-throughput-opacity]="slot.throughputOpacityCss"
              >
                <span></span>
              </span>
            }
          </div>
        }
        @if (showContinuityRail) {
          <div class="fluid-capacity-hour__continuity" aria-hidden="true">
            @for (slot of renderedSlots; track slot.id) {
              <span
                class="fluid-capacity-hour__continuity-segment"
                [style.left.%]="slot.leftPercent"
                [style.width.%]="slot.widthPercent"
                [style.--fluid-throughput-color]="slot.throughputColorCss"
                [style.--fluid-pressure-opacity]="slot.pressureOpacityCss"
              ></span>
            }
          </div>
        }
        @for (slot of renderedSlots; track slot.id) {
          <button
            type="button"
            class="fluid-capacity-hour__slot"
            [class.fluid-capacity-hour__slot--unavailable]="(slot.specialistAvailable <= 0 || slot.deviceAvailable <= 0) && !slot.isBusinessSuccess"
            [class.fluid-capacity-hour__slot--dead-throughput]="slot.isDeadThroughput"
            [class.fluid-capacity-hour__slot--business-success]="slot.isBusinessSuccess"
            [class.fluid-capacity-hour__slot--near-business-success]="slot.isNearBusinessSuccess"
            [class.fluid-capacity-hour__slot--rail-healthy]="slot.railState === 'healthy'"
            [class.fluid-capacity-hour__slot--rail-constrained]="slot.railState === 'constrained'"
            [class.fluid-capacity-hour__slot--rail-fragile]="slot.railState === 'fragile'"
            [class.fluid-capacity-hour__slot--rail-dead]="slot.railState === 'dead'"
            [class.fluid-capacity-hour__slot--rail-recovery]="slot.railState === 'recovery'"
            [class.fluid-capacity-hour__slot--reserved]="slot.status === 'reserved'"
            [class.fluid-capacity-hour__slot--continues-from-previous]="slot.continuesFromPrevious"
            [class.fluid-capacity-hour__slot--continues-to-next]="slot.continuesToNext"
            [class.fluid-capacity-hour__slot--bottleneck-device]="slot.bottleneckKind === 'device'"
            [class.fluid-capacity-hour__slot--bottleneck-specialist]="slot.bottleneckKind === 'specialist'"
            [class.fluid-capacity-hour__slot--bottleneck-both]="slot.bottleneckKind === 'both'"
            [class.fluid-capacity-hour__slot--zero-device]="slot.deviceAvailable <= 0 && slot.deviceTotal > 0 && !slot.isBusinessSuccess"
            [class.fluid-capacity-hour__slot--zero-specialist]="slot.specialistAvailable <= 0 && slot.specialistTotal > 0 && !slot.isBusinessSuccess"
            [class.fluid-capacity-hour__slot--pressure-overlay]="slot.showPressureOverlay"
            [class.fluid-capacity-hour__slot--state-layer]="slot.showStateLayer"
            [class.fluid-capacity-hour__slot--state-stable]="slot.operationalState === 'stable'"
            [class.fluid-capacity-hour__slot--state-unstable]="slot.operationalState === 'unstable'"
            [class.fluid-capacity-hour__slot--state-overloaded]="slot.operationalState === 'overloaded'"
            [class.fluid-capacity-hour__slot--state-recovery]="slot.operationalState === 'recovery'"
            [class.fluid-capacity-hour__slot--state-predicted]="slot.operationalState === 'predicted-saturation'"
            [class.fluid-capacity-hour__slot--signal-healthy]="slot.intelligenceSignal === 'healthy'"
            [class.fluid-capacity-hour__slot--signal-blocked]="slot.intelligenceSignal === 'blocked'"
            [class.fluid-capacity-hour__slot--signal-expand]="slot.intelligenceSignal === 'expand'"
            [class.fluid-capacity-hour__slot--signal-hire-specialists]="slot.intelligenceSignal === 'hire-specialists'"
            [class.fluid-capacity-hour__slot--signal-add-devices]="slot.intelligenceSignal === 'add-devices'"
            [class.fluid-capacity-hour__slot--signal-preference-skew]="slot.intelligenceSignal === 'preference-skew'"
            [class.fluid-capacity-hour__slot--signal-mix-waste]="slot.intelligenceSignal === 'mix-waste'"
            [class.fluid-capacity-hour__slot--signal-weak]="slot.intelligenceStrength === 'weak'"
            [class.fluid-capacity-hour__slot--signal-strong]="slot.intelligenceStrength === 'strong'"
            [class.fluid-capacity-hour__slot--signal-chronic]="slot.intelligenceStrength === 'chronic'"
            [class.fluid-capacity-hour__slot--signal-repeats]="slot.repeatsIntelligenceSignal"
            [style.left.%]="slot.leftPercent"
            [style.width.%]="slot.widthPercent"
            [style.--fluid-split-left-y]="slot.splitLeftCss"
            [style.--fluid-split-right-y]="slot.splitRightCss"
            [style.--fluid-device-start]="slot.deviceColorStart"
            [style.--fluid-device-end]="slot.deviceColorEnd"
            [style.--fluid-specialist-start]="slot.specialistColorStart"
            [style.--fluid-specialist-end]="slot.specialistColorEnd"
            [style.--fluid-throughput-width]="slot.throughputWidthCss"
            [style.--fluid-throughput-opacity]="slot.throughputOpacityCss"
            [style.--fluid-throughput-color]="slot.throughputColorCss"
            [style.--fluid-pressure-opacity]="slot.pressureOpacityCss"
            [style.--fluid-color-flow-opacity]="slot.flowOpacityCss"
            [style.--fluid-device-required-time]="slot.deviceRequiredTimeCss"
            [style.--fluid-specialist-only-time]="slot.specialistOnlyTimeCss"
            [style.--fluid-specialist-idle]="slot.specialistIdleCss"
            [style.--fluid-device-idle]="slot.deviceIdleCss"
            [style.--fluid-preference-skew]="slot.preferenceSkewCss"
            [title]="slotTitle(slot)"
          >
            <svg
              class="fluid-capacity-hour__regions"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden="true"
              focusable="false"
            >
              <defs>
                <linearGradient [attr.id]="slot.deviceGradientId" x1="0" y1="0" x2="100" y2="100" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" [attr.stop-color]="slot.deviceColorStart" />
                  <stop offset="100%" [attr.stop-color]="slot.deviceColorEnd" />
                </linearGradient>
                <linearGradient [attr.id]="slot.deviceFlowGradientId" x1="0" y1="0" [attr.x2]="colorFlowWidth" y2="0" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" [attr.stop-color]="slot.deviceFlowInletColor" />
                  <stop offset="100%" [attr.stop-color]="slot.deviceColorStart" />
                </linearGradient>
                <linearGradient [attr.id]="slot.specialistGradientId" x1="0" y1="0" x2="100" y2="100" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" [attr.stop-color]="slot.specialistColorStart" />
                  <stop offset="100%" [attr.stop-color]="slot.specialistColorEnd" />
                </linearGradient>
                <linearGradient [attr.id]="slot.specialistFlowGradientId" x1="0" y1="0" [attr.x2]="colorFlowWidth" y2="0" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" [attr.stop-color]="slot.specialistFlowInletColor" />
                  <stop offset="100%" [attr.stop-color]="slot.specialistColorStart" />
                </linearGradient>
              </defs>
              <path [attr.d]="slot.devicePath" [attr.fill]="'url(#' + slot.deviceGradientId + ')'"></path>
              <path [attr.d]="slot.specialistPath" [attr.fill]="'url(#' + slot.specialistGradientId + ')'"></path>
              @if (slot.hasFlowCarry) {
                <path
                  class="fluid-capacity-hour__flow"
                  [attr.d]="slot.deviceFlowPath"
                  [attr.fill]="'url(#' + slot.deviceFlowGradientId + ')'"
                ></path>
                <path
                  class="fluid-capacity-hour__flow"
                  [attr.d]="slot.specialistFlowPath"
                  [attr.fill]="'url(#' + slot.specialistFlowGradientId + ')'"
                ></path>
              }
              <path
                class="fluid-capacity-hour__target-fill fluid-capacity-hour__target-fill--device"
                [attr.d]="slot.deviceTargetBoundaryPath"
              ></path>
              <path
                class="fluid-capacity-hour__target-fill fluid-capacity-hour__target-fill--specialist"
                [attr.d]="slot.specialistTargetBoundaryPath"
              ></path>
              <path class="fluid-capacity-hour__boundary" [attr.d]="slot.boundaryPath"></path>
            </svg>
            <span class="fluid-capacity-hour__content">
              <span class="fluid-capacity-hour__minute fluid-capacity-hour__minute--start">{{ slot.startMinuteLabel }}</span>
              <span class="fluid-capacity-hour__minute fluid-capacity-hour__minute--end">{{ slot.endMinuteLabel }}</span>
              <span class="fluid-capacity-hour__time">{{ slot.startLabel }}-{{ slot.endLabel }}</span>
              <span class="fluid-capacity-hour__metric fluid-capacity-hour__metric--device">
                D {{ slot.deviceAvailable }}/{{ slot.deviceTotal }}
              </span>
              <span class="fluid-capacity-hour__metric fluid-capacity-hour__metric--specialist">
                S {{ slot.specialistAvailable }}/{{ slot.specialistTotal }}
              </span>
              <span class="fluid-capacity-hour__resource-summary">{{ slot.resourceSummaryLabel }}</span>
              @if (slot.showThroughput) {
                <span class="fluid-capacity-hour__throughput" aria-hidden="true">
                  <span class="fluid-capacity-hour__throughput-fill"></span>
                </span>
              }
              @if (slot.showStateLayer) {
                <span class="fluid-capacity-hour__state" aria-hidden="true"></span>
              }
              @if (slot.showCapacityIntelligence) {
                <span
                  class="fluid-capacity-hour__intelligence"
                  [class.fluid-capacity-hour__intelligence--compact]="renderVariant === 'capacity-intelligence-compact' || renderVariant === 'capacity-intelligence-maturity'"
                  aria-hidden="true"
                >
                  <span class="fluid-capacity-hour__intelligence-head">
                    <span class="fluid-capacity-hour__intelligence-label">{{ slot.intelligenceLabel }}</span>
                    <span class="fluid-capacity-hour__intelligence-follow">{{ slot.intelligenceFollowUp }}</span>
                  </span>
                  <span class="fluid-capacity-hour__mix">
                    <span class="fluid-capacity-hour__mix-device"></span>
                    <span class="fluid-capacity-hour__mix-specialist"></span>
                  </span>
                  <span class="fluid-capacity-hour__idle fluid-capacity-hour__idle--specialist"></span>
                  <span class="fluid-capacity-hour__idle fluid-capacity-hour__idle--device"></span>
                  <span class="fluid-capacity-hour__preference"></span>
                </span>
              }
            </span>
          </button>
        }
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      min-width: 0;
      max-width: 100%;
    }

    .fluid-capacity-hour {
      display: grid;
      grid-template-columns: 54px minmax(0, 1fr);
      gap: 10px;
      align-items: stretch;
      color: #111827;
      min-width: 0;
      max-width: 100%;
    }

    .fluid-capacity-hour__label {
      padding-top: 8px;
      font-size: 13px;
      font-weight: 700;
      color: #374151;
      text-align: right;
    }

    .fluid-capacity-hour__track {
      min-height: var(--fluid-hour-min-height, 60px);
      position: relative;
      border-radius: 10px;
      background: #f8fafc;
      border: 1px solid #e5e7eb;
      overflow: hidden;
      min-width: 0;
    }

    .fluid-capacity-hour__slot {
      position: absolute;
      top: 5px;
      bottom: 5px;
      border: 1px solid rgba(15, 23, 42, 0.16);
      border-radius: 10px;
      padding: 0;
      overflow: hidden;
      background: #ffffff;
      cursor: default;
      text-align: left;
      min-width: 1px;
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
    }

    .fluid-capacity-hour--throughput-rail .fluid-capacity-hour__slot,
    .fluid-capacity-hour--continuity-rail .fluid-capacity-hour__slot {
      bottom: 10px;
    }

    .fluid-capacity-hour__hour-throughput,
    .fluid-capacity-hour__continuity {
      position: absolute;
      left: 5px;
      right: 5px;
      bottom: 4px;
      height: 4px;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.58);
      overflow: hidden;
      z-index: 5;
      pointer-events: none;
      box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.08);
    }

    .fluid-capacity-hour__hour-throughput-segment,
    .fluid-capacity-hour__continuity-segment {
      position: absolute;
      top: 0;
      bottom: 0;
      overflow: hidden;
      opacity: var(--fluid-throughput-opacity, 1);
    }

    .fluid-capacity-hour__hour-throughput-segment > span {
      display: block;
      width: var(--fluid-throughput-width, 0%);
      height: 100%;
      border-radius: 999px;
      background: var(--fluid-throughput-color, rgba(15, 23, 42, 0.44));
    }

    .fluid-capacity-hour__continuity {
      bottom: 5px;
      height: 2px;
      background: rgba(148, 163, 184, 0.16);
      box-shadow: none;
    }

    .fluid-capacity-hour__continuity-segment {
      background: var(--fluid-throughput-color, rgba(148, 163, 184, 0.42));
      opacity: var(--fluid-pressure-opacity, 0.35);
    }

    .fluid-capacity-hour__slot::before,
    .fluid-capacity-hour__slot::after {
      content: "";
      position: absolute;
      pointer-events: none;
      z-index: 2;
      opacity: 0;
    }

    .fluid-capacity-hour__slot--continues-from-previous {
      border-top-left-radius: 7px;
      border-bottom-left-radius: 7px;
      border-left-color: rgba(15, 23, 42, 0.08);
    }

    .fluid-capacity-hour__slot--continues-to-next {
      border-top-right-radius: 7px;
      border-bottom-right-radius: 7px;
      border-right-color: rgba(15, 23, 42, 0.08);
    }

    .fluid-capacity-hour__slot--continues-to-next::after {
      content: "";
      position: absolute;
      top: 7px;
      right: -1px;
      bottom: 7px;
      width: 1px;
      background: rgba(255, 255, 255, 0.24);
      z-index: 3;
      opacity: 1;
      pointer-events: none;
    }

    .fluid-capacity-hour__slot--reserved {
      border-color: rgba(245, 158, 11, 0.45);
    }

    .fluid-capacity-hour__slot--unavailable {
      border-color: rgba(239, 68, 68, 0.52);
    }

    .fluid-capacity-hour--bottleneck-scan .fluid-capacity-hour__slot--dead-throughput {
      filter: saturate(0.68);
      box-shadow:
        inset 0 0 0 1px rgba(220, 38, 38, 0.26),
        0 1px 2px rgba(15, 23, 42, 0.06);
    }

    .fluid-capacity-hour--bottleneck-scan .fluid-capacity-hour__slot--dead-throughput .fluid-capacity-hour__regions {
      opacity: 0.78;
    }

    .fluid-capacity-hour__slot--pressure-overlay::before {
      inset: 0;
      border-radius: inherit;
      opacity: var(--fluid-pressure-opacity, 0.35);
    }

    .fluid-capacity-hour__slot--pressure-overlay.fluid-capacity-hour__slot--bottleneck-device::before {
      box-shadow: inset 0 3px 0 rgba(220, 38, 38, 0.74);
    }

    .fluid-capacity-hour__slot--pressure-overlay.fluid-capacity-hour__slot--bottleneck-specialist::before {
      box-shadow: inset 0 -3px 0 rgba(220, 38, 38, 0.74);
    }

    .fluid-capacity-hour__slot--pressure-overlay.fluid-capacity-hour__slot--bottleneck-both::before {
      border: 2px solid rgba(220, 38, 38, 0.74);
      background:
        repeating-linear-gradient(
          -45deg,
          rgba(220, 38, 38, 0.16) 0,
          rgba(220, 38, 38, 0.16) 4px,
          rgba(255, 255, 255, 0) 4px,
          rgba(255, 255, 255, 0) 9px
        );
    }

    .fluid-capacity-hour__regions {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
    }

    .fluid-capacity-hour__boundary {
      fill: none;
      stroke: rgba(255, 255, 255, 0.86);
      stroke-width: 1.2;
      vector-effect: non-scaling-stroke;
    }

    .fluid-capacity-hour--capacity-intelligence .fluid-capacity-hour__boundary,
    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__boundary,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__boundary {
      stroke: rgba(255, 255, 255, 0.9);
      stroke-width: 1.1;
    }

    .fluid-capacity-hour__target-fill {
      display: none;
      pointer-events: none;
    }

    .fluid-capacity-hour__target-fill--device {
      fill: rgba(34, 197, 94, 0.28);
    }

    .fluid-capacity-hour__target-fill--specialist {
      fill: rgba(147, 197, 253, 0.32);
    }

    .fluid-capacity-hour--capacity-intelligence .fluid-capacity-hour__target-fill,
    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__target-fill,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__target-fill {
      display: block;
    }

    .fluid-capacity-hour__flow {
      opacity: var(--fluid-color-flow-opacity, 0.55);
    }

    .fluid-capacity-hour__content {
      position: absolute;
      inset: 6px;
      display: grid;
      grid-template-areas:
        "time device"
        "specialist specialist";
      grid-template-columns: 1fr 1fr;
      align-content: space-between;
      gap: 2px 6px;
      color: rgba(15, 23, 42, 0.88);
      text-shadow: 0 1px 1px rgba(255, 255, 255, 0.46);
      min-width: 0;
      z-index: 4;
    }

    .fluid-capacity-hour__time {
      grid-area: time;
      font-size: 10px;
      line-height: 1.1;
      font-weight: 800;
      min-width: 0;
      overflow: hidden;
      text-overflow: clip;
      overflow-wrap: normal;
      white-space: nowrap;
    }

    .fluid-capacity-hour__metric {
      font-size: 10px;
      line-height: 1.1;
      font-weight: 800;
      min-width: 0;
      overflow: hidden;
      text-overflow: clip;
      overflow-wrap: normal;
      white-space: nowrap;
    }

    .fluid-capacity-hour__metric--device {
      grid-area: device;
      text-align: right;
    }

    .fluid-capacity-hour__metric--specialist {
      grid-area: specialist;
    }

    .fluid-capacity-hour__minute,
    .fluid-capacity-hour__resource-summary {
      display: none;
      min-width: 0;
      overflow: hidden;
      text-overflow: clip;
      overflow-wrap: normal;
      white-space: nowrap;
      font-weight: 800;
      line-height: 1;
      text-shadow: 0 1px 1px rgba(255, 255, 255, 0.46);
    }

    .fluid-capacity-hour__minute--start {
      grid-area: minute-start;
    }

    .fluid-capacity-hour__minute--end {
      grid-area: minute-end;
      text-align: right;
    }

    .fluid-capacity-hour__resource-summary {
      grid-area: resource-summary;
      align-self: start;
      justify-self: center;
      font-size: 9px;
      text-align: center;
    }

    .fluid-capacity-hour__throughput {
      position: absolute;
      left: 50%;
      top: 50%;
      width: min(72px, 58%);
      height: 4px;
      transform: translateX(-50%);
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.52);
      box-shadow:
        inset 0 0 0 1px rgba(15, 23, 42, 0.1),
        0 1px 1px rgba(255, 255, 255, 0.3);
      overflow: hidden;
      opacity: var(--fluid-throughput-opacity, 1);
      z-index: 1;
    }

    .fluid-capacity-hour__throughput-fill {
      display: block;
      width: var(--fluid-throughput-width, 0%);
      height: 100%;
      border-radius: inherit;
      background: var(--fluid-throughput-color, rgba(15, 23, 42, 0.38));
    }

    .fluid-capacity-hour--throughput-band .fluid-capacity-hour__throughput {
      left: 8px;
      right: 8px;
      top: 50%;
      width: auto;
      height: 6px;
      transform: translateY(-50%);
      background: rgba(255, 255, 255, 0.46);
    }

    .fluid-capacity-hour--throughput-band .fluid-capacity-hour__throughput-fill {
      background: var(--fluid-throughput-color, rgba(15, 23, 42, 0.48));
    }

    .fluid-capacity-hour--operational-candidate .fluid-capacity-hour__slot {
      border-color: rgba(15, 23, 42, 0.12);
    }

    .fluid-capacity-hour--operational-candidate .fluid-capacity-hour__throughput {
      left: 10px;
      right: 10px;
      top: 56%;
      width: auto;
      height: 6px;
      transform: translateY(-50%);
      background: rgba(255, 255, 255, 0.22);
      box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.34);
    }

    .fluid-capacity-hour--operational-candidate .fluid-capacity-hour__throughput-fill {
      background: var(--fluid-throughput-color, rgba(15, 23, 42, 0.46));
      box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.28);
    }

    .fluid-capacity-hour--operational-candidate .fluid-capacity-hour__slot--dead-throughput .fluid-capacity-hour__throughput-fill {
      width: 7px !important;
      background: rgba(220, 38, 38, 0.72);
    }

    .fluid-capacity-hour--operational-candidate .fluid-capacity-hour__slot--pressure-overlay.fluid-capacity-hour__slot--bottleneck-device::before {
      box-shadow: inset 0 2px 0 rgba(217, 119, 6, 0.5);
    }

    .fluid-capacity-hour--operational-candidate .fluid-capacity-hour__slot--pressure-overlay.fluid-capacity-hour__slot--bottleneck-specialist::before {
      box-shadow: inset 0 -2px 0 rgba(217, 119, 6, 0.5);
    }

    .fluid-capacity-hour--operational-candidate .fluid-capacity-hour__slot--dead-throughput.fluid-capacity-hour__slot--pressure-overlay.fluid-capacity-hour__slot--bottleneck-device::before {
      box-shadow: inset 0 3px 0 rgba(220, 38, 38, 0.72);
    }

    .fluid-capacity-hour--operational-candidate .fluid-capacity-hour__slot--dead-throughput.fluid-capacity-hour__slot--pressure-overlay.fluid-capacity-hour__slot--bottleneck-specialist::before {
      box-shadow: inset 0 -3px 0 rgba(220, 38, 38, 0.72);
    }

    .fluid-capacity-hour--operational-candidate .fluid-capacity-hour__slot--pressure-overlay.fluid-capacity-hour__slot--bottleneck-both::before {
      border: 2px solid rgba(220, 38, 38, 0.62);
      background: none;
    }

    .fluid-capacity-hour--capacity-intelligence .fluid-capacity-hour__slot {
      border-color: rgba(15, 23, 42, 0.12);
    }

    .fluid-capacity-hour--capacity-intelligence .fluid-capacity-hour__content {
      inset: 6px;
      grid-template-areas:
        "time device"
        "specialist specialist"
        "insight insight";
      grid-template-rows: auto 1fr auto;
      align-content: stretch;
    }

    .fluid-capacity-hour--capacity-intelligence .fluid-capacity-hour__throughput {
      left: 10px;
      right: 10px;
      top: 46%;
      width: auto;
      height: 5px;
      transform: translateY(-50%);
      background: rgba(255, 255, 255, 0.22);
      box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.32);
    }

    .fluid-capacity-hour--capacity-intelligence .fluid-capacity-hour__throughput-fill {
      background: var(--fluid-throughput-color, rgba(15, 23, 42, 0.46));
    }

    .fluid-capacity-hour--capacity-intelligence .fluid-capacity-hour__slot--dead-throughput .fluid-capacity-hour__throughput-fill {
      width: 7px !important;
      background: rgba(220, 38, 38, 0.74);
    }

    .fluid-capacity-hour--capacity-intelligence .fluid-capacity-hour__slot--pressure-overlay.fluid-capacity-hour__slot--bottleneck-device::before {
      box-shadow: inset 0 2px 0 rgba(217, 119, 6, 0.55);
    }

    .fluid-capacity-hour--capacity-intelligence .fluid-capacity-hour__slot--pressure-overlay.fluid-capacity-hour__slot--bottleneck-specialist::before {
      box-shadow: inset 0 -2px 0 rgba(217, 119, 6, 0.55);
    }

    .fluid-capacity-hour--capacity-intelligence .fluid-capacity-hour__slot--dead-throughput.fluid-capacity-hour__slot--pressure-overlay.fluid-capacity-hour__slot--bottleneck-device::before {
      box-shadow: inset 0 3px 0 rgba(220, 38, 38, 0.74);
    }

    .fluid-capacity-hour--capacity-intelligence .fluid-capacity-hour__slot--dead-throughput.fluid-capacity-hour__slot--pressure-overlay.fluid-capacity-hour__slot--bottleneck-specialist::before {
      box-shadow: inset 0 -3px 0 rgba(220, 38, 38, 0.74);
    }

    .fluid-capacity-hour--capacity-intelligence .fluid-capacity-hour__slot--pressure-overlay.fluid-capacity-hour__slot--bottleneck-both::before {
      border: 2px solid rgba(220, 38, 38, 0.62);
      background: none;
    }

    .fluid-capacity-hour--capacity-intelligence .fluid-capacity-hour__flow,
    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__flow,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__flow {
      display: none;
    }

    .fluid-capacity-hour--capacity-intelligence .fluid-capacity-hour__slot--business-success,
    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__slot--business-success,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__slot--business-success {
      border-color: rgba(22, 163, 74, 0.74);
      box-shadow:
        inset 0 0 0 1px rgba(22, 163, 74, 0.28),
        0 1px 2px rgba(15, 23, 42, 0.08);
    }

    .fluid-capacity-hour--capacity-intelligence .fluid-capacity-hour__slot--near-business-success,
    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__slot--near-business-success,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__slot--near-business-success {
      border-color: rgba(22, 163, 74, 0.38);
      box-shadow:
        inset 0 0 0 1px rgba(22, 163, 74, 0.12),
        0 1px 2px rgba(15, 23, 42, 0.08);
    }

    .fluid-capacity-hour--capacity-intelligence .fluid-capacity-hour__slot--business-success.fluid-capacity-hour__slot--pressure-overlay::before,
    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__slot--business-success.fluid-capacity-hour__slot--pressure-overlay::before,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__slot--business-success.fluid-capacity-hour__slot--pressure-overlay::before {
      background: none;
    }

    .fluid-capacity-hour__intelligence {
      grid-area: insight;
      position: relative;
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto auto;
      grid-template-areas:
        "head head head"
        "mix specialist device";
      gap: 3px 5px;
      align-items: center;
      min-width: 0;
    }

    .fluid-capacity-hour__intelligence-head {
      grid-area: head;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 4px;
      min-width: 0;
    }

    .fluid-capacity-hour__intelligence-label,
    .fluid-capacity-hour__intelligence-follow {
      min-width: 0;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      line-height: 1;
      font-weight: 800;
      text-shadow: 0 1px 1px rgba(255, 255, 255, 0.5);
    }

    .fluid-capacity-hour__intelligence-label {
      font-size: 8px;
      color: rgba(15, 23, 42, 0.88);
    }

    .fluid-capacity-hour__intelligence-follow {
      flex: 0 0 auto;
      font-size: 7px;
      color: rgba(15, 23, 42, 0.58);
    }

    .fluid-capacity-hour__mix {
      grid-area: mix;
      position: relative;
      height: 5px;
      border-radius: 999px;
      overflow: hidden;
      background: rgba(255, 255, 255, 0.42);
      box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.16);
    }

    .fluid-capacity-hour__mix-device,
    .fluid-capacity-hour__mix-specialist {
      position: absolute;
      top: 0;
      bottom: 0;
    }

    .fluid-capacity-hour__mix-device {
      left: 0;
      width: var(--fluid-device-required-time, 0%);
      background: rgba(34, 197, 94, 0.3);
    }

    .fluid-capacity-hour__mix-specialist {
      right: 0;
      width: var(--fluid-specialist-only-time, 0%);
      background: rgba(59, 130, 246, 0.38);
    }

    .fluid-capacity-hour__idle {
      width: 14px;
      height: 5px;
      border-radius: 999px;
      background: rgba(15, 23, 42, 0.12);
      box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.14);
    }

    .fluid-capacity-hour__idle--specialist {
      grid-area: specialist;
      background:
        linear-gradient(
          90deg,
          rgba(59, 130, 246, 0.55) var(--fluid-specialist-idle, 0%),
          rgba(255, 255, 255, 0.36) var(--fluid-specialist-idle, 0%)
        );
    }

    .fluid-capacity-hour__idle--device {
      grid-area: device;
      background:
        linear-gradient(
          90deg,
          rgba(34, 197, 94, 0.55) var(--fluid-device-idle, 0%),
          rgba(255, 255, 255, 0.36) var(--fluid-device-idle, 0%)
        );
    }

    .fluid-capacity-hour__preference {
      position: absolute;
      right: 0;
      top: -16px;
      width: 5px;
      height: max(5px, var(--fluid-preference-skew, 0%));
      max-height: 16px;
      border-radius: 999px;
      background: rgba(99, 102, 241, 0.58);
      box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.58);
    }

    .fluid-capacity-hour--capacity-intelligence .fluid-capacity-hour__slot--signal-healthy .fluid-capacity-hour__intelligence-label,
    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__slot--signal-healthy .fluid-capacity-hour__intelligence-label,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__slot--signal-healthy .fluid-capacity-hour__intelligence-label {
      color: rgba(21, 128, 61, 0.9);
    }

    .fluid-capacity-hour--capacity-intelligence .fluid-capacity-hour__slot--signal-expand .fluid-capacity-hour__intelligence-label,
    .fluid-capacity-hour--capacity-intelligence .fluid-capacity-hour__slot--signal-add-devices .fluid-capacity-hour__intelligence-label,
    .fluid-capacity-hour--capacity-intelligence .fluid-capacity-hour__slot--signal-hire-specialists .fluid-capacity-hour__intelligence-label,
    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__slot--signal-expand .fluid-capacity-hour__intelligence-label,
    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__slot--signal-add-devices .fluid-capacity-hour__intelligence-label,
    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__slot--signal-hire-specialists .fluid-capacity-hour__intelligence-label,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__slot--signal-add-devices .fluid-capacity-hour__intelligence-label,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__slot--signal-hire-specialists .fluid-capacity-hour__intelligence-label {
      color: rgba(185, 28, 28, 0.88);
    }

    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__slot--signal-expand .fluid-capacity-hour__intelligence-label {
      color: rgba(21, 128, 61, 0.95);
    }

    .fluid-capacity-hour--capacity-intelligence .fluid-capacity-hour__slot--signal-mix-waste .fluid-capacity-hour__intelligence-label,
    .fluid-capacity-hour--capacity-intelligence .fluid-capacity-hour__slot--signal-preference-skew .fluid-capacity-hour__intelligence-label,
    .fluid-capacity-hour--capacity-intelligence .fluid-capacity-hour__slot--signal-blocked .fluid-capacity-hour__intelligence-label,
    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__slot--signal-mix-waste .fluid-capacity-hour__intelligence-label,
    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__slot--signal-preference-skew .fluid-capacity-hour__intelligence-label,
    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__slot--signal-blocked .fluid-capacity-hour__intelligence-label,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__slot--signal-mix-waste .fluid-capacity-hour__intelligence-label,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__slot--signal-preference-skew .fluid-capacity-hour__intelligence-label,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__slot--signal-blocked .fluid-capacity-hour__intelligence-label {
      color: rgba(146, 64, 14, 0.74);
    }

    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__slot,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__slot {
      border-color: rgba(15, 23, 42, 0.12);
    }

    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__content,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__content {
      grid-template-areas:
        "minute-start device minute-end"
        ". . ."
        "specialist specialist specialist";
      grid-template-columns: auto minmax(0, 1fr) auto;
      grid-template-rows: auto 1fr auto;
      align-content: stretch;
    }

    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__time,
    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__resource-summary,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__time,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__resource-summary {
      display: none;
    }

    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__minute,
    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__metric,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__minute,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__metric {
      display: block;
      color: rgba(15, 23, 42, 0.88);
    }

    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__minute,
    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__metric,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__minute,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__metric {
      font-size: 10px;
    }

    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__metric--device,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__metric--device {
      align-self: start;
      justify-self: center;
      text-align: center;
    }

    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__metric--specialist,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__metric--specialist {
      align-self: end;
      justify-self: center;
      text-align: center;
    }

    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__throughput,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__throughput {
      left: 10px;
      right: 10px;
      top: 50%;
      width: auto;
      height: 6px;
      transform: translateY(-50%);
      background: rgba(255, 255, 255, 0.22);
      box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.34);
    }

    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__slot--dead-throughput .fluid-capacity-hour__throughput-fill {
      width: 7px !important;
      background: rgba(220, 38, 38, 0.74);
    }

    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__slot--pressure-overlay.fluid-capacity-hour__slot--bottleneck-device::before,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__slot--pressure-overlay.fluid-capacity-hour__slot--bottleneck-device::before {
      box-shadow: inset 0 2px 0 rgba(217, 119, 6, 0.5);
    }

    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__slot--pressure-overlay.fluid-capacity-hour__slot--bottleneck-specialist::before,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__slot--pressure-overlay.fluid-capacity-hour__slot--bottleneck-specialist::before {
      box-shadow: inset 0 -2px 0 rgba(217, 119, 6, 0.5);
    }

    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__slot--dead-throughput.fluid-capacity-hour__slot--pressure-overlay.fluid-capacity-hour__slot--bottleneck-device::before,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__slot--dead-throughput.fluid-capacity-hour__slot--pressure-overlay.fluid-capacity-hour__slot--bottleneck-device::before {
      box-shadow: inset 0 3px 0 rgba(220, 38, 38, 0.74);
    }

    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__slot--zero-device.fluid-capacity-hour__slot--pressure-overlay::before,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__slot--zero-device.fluid-capacity-hour__slot--pressure-overlay::before {
      box-shadow: inset 0 4px 0 rgba(185, 28, 28, 0.9);
    }

    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__slot--dead-throughput.fluid-capacity-hour__slot--pressure-overlay.fluid-capacity-hour__slot--bottleneck-specialist::before,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__slot--dead-throughput.fluid-capacity-hour__slot--pressure-overlay.fluid-capacity-hour__slot--bottleneck-specialist::before {
      box-shadow: inset 0 -3px 0 rgba(220, 38, 38, 0.74);
    }

    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__slot--zero-specialist.fluid-capacity-hour__slot--pressure-overlay::before,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__slot--zero-specialist.fluid-capacity-hour__slot--pressure-overlay::before {
      box-shadow: inset 0 -4px 0 rgba(185, 28, 28, 0.9);
    }

    .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__slot--pressure-overlay.fluid-capacity-hour__slot--bottleneck-both::before,
    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__slot--pressure-overlay.fluid-capacity-hour__slot--bottleneck-both::before {
      border: 1px solid rgba(217, 119, 6, 0.34);
      background: none;
      box-shadow:
        inset 0 2px 0 rgba(217, 119, 6, 0.45),
        inset 0 -2px 0 rgba(217, 119, 6, 0.45);
    }

    .fluid-capacity-hour__intelligence--compact {
      position: absolute;
      left: 10px;
      right: 10px;
      top: 50%;
      transform: translateY(-50%);
      z-index: 2;
      grid-template-columns: minmax(0, 1fr);
      grid-template-areas: "head";
      align-self: center;
    }

    .fluid-capacity-hour__intelligence--compact .fluid-capacity-hour__intelligence-head {
      min-width: 0;
      padding: 0;
      border-radius: 0;
      background: transparent;
      box-shadow: none;
    }

    .fluid-capacity-hour__intelligence--compact .fluid-capacity-hour__intelligence-label,
    .fluid-capacity-hour__intelligence--compact .fluid-capacity-hour__intelligence-follow {
      padding: 1px 4px;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.72);
      box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.1);
    }

    .fluid-capacity-hour__intelligence--compact .fluid-capacity-hour__intelligence-label {
      font-size: 8px;
    }

    .fluid-capacity-hour__intelligence--compact .fluid-capacity-hour__intelligence-follow {
      font-size: 7px;
      max-width: 44%;
    }

    .fluid-capacity-hour__intelligence--compact .fluid-capacity-hour__mix,
    .fluid-capacity-hour__intelligence--compact .fluid-capacity-hour__idle,
    .fluid-capacity-hour__intelligence--compact .fluid-capacity-hour__preference {
      display: none;
    }

    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__slot--signal-weak .fluid-capacity-hour__intelligence-label::after {
      content: "?";
      margin-left: 1px;
      opacity: 0.72;
    }

    .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__slot--signal-expand .fluid-capacity-hour__intelligence--compact .fluid-capacity-hour__intelligence-label {
      background: rgba(220, 252, 231, 0.64);
      box-shadow:
        inset 3px 0 0 rgba(22, 163, 74, 0.42),
        inset 0 0 0 1px rgba(22, 163, 74, 0.18);
    }

    .fluid-capacity-hour--medium-pressure .fluid-capacity-hour__slot {
      border-color: rgba(15, 23, 42, 0.12);
    }

    .fluid-capacity-hour--medium-pressure .fluid-capacity-hour__slot--bottleneck-device,
    .fluid-capacity-hour--medium-pressure .fluid-capacity-hour__slot--bottleneck-specialist {
      box-shadow:
        inset 0 0 0 1px rgba(245, 158, 11, 0.24),
        0 1px 2px rgba(15, 23, 42, 0.08);
    }

    .fluid-capacity-hour--medium-pressure .fluid-capacity-hour__slot--dead-throughput {
      border-color: rgba(220, 38, 38, 0.68);
      box-shadow:
        inset 0 0 0 1px rgba(220, 38, 38, 0.34),
        0 1px 2px rgba(15, 23, 42, 0.08);
    }

    .fluid-capacity-hour--focus-flow .fluid-capacity-hour__boundary {
      stroke-width: 1.45;
      stroke: rgba(255, 255, 255, 0.9);
    }

    .fluid-capacity-hour--focus-flow .fluid-capacity-hour__flow {
      opacity: calc(var(--fluid-color-flow-opacity, 0.55) + 0.08);
    }

    .fluid-capacity-hour__state {
      position: absolute;
      right: 6px;
      bottom: 6px;
      width: 6px;
      height: 6px;
      border-radius: 999px;
      background: rgba(15, 23, 42, 0.3);
      box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.62);
    }

    .fluid-capacity-hour__slot--state-stable .fluid-capacity-hour__state {
      background: rgba(16, 185, 129, 0.64);
    }

    .fluid-capacity-hour__slot--state-unstable .fluid-capacity-hour__state,
    .fluid-capacity-hour__slot--state-predicted .fluid-capacity-hour__state {
      background: rgba(245, 158, 11, 0.72);
    }

    .fluid-capacity-hour__slot--state-overloaded .fluid-capacity-hour__state {
      background: rgba(220, 38, 38, 0.78);
    }

    .fluid-capacity-hour__slot--state-recovery .fluid-capacity-hour__state {
      background: linear-gradient(90deg, rgba(245, 158, 11, 0.72), rgba(34, 197, 94, 0.7));
    }

    @media (max-width: 760px) {
      .fluid-capacity-hour {
        grid-template-columns: 30px minmax(0, 1fr);
        gap: 4px;
      }

      .fluid-capacity-hour__label {
        padding-top: 7px;
        font-size: 12px;
      }

      .fluid-capacity-hour__slot {
        top: 4px;
        bottom: 4px;
        border-radius: 8px;
      }

      .fluid-capacity-hour__content {
        inset: 4px;
        gap: 1px 3px;
      }

      .fluid-capacity-hour__time,
      .fluid-capacity-hour__metric,
      .fluid-capacity-hour__minute,
      .fluid-capacity-hour__resource-summary {
        font-size: 9px;
        line-height: 1;
      }

      .fluid-capacity-hour__resource-summary {
        font-size: 8px;
      }

      .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__content,
      .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__content {
        inset: 4px;
      }

      .fluid-capacity-hour--capacity-intelligence-compact .fluid-capacity-hour__throughput,
      .fluid-capacity-hour--capacity-intelligence-maturity .fluid-capacity-hour__throughput {
        left: 5px;
        right: 5px;
        height: 5px;
      }

      .fluid-capacity-hour__intelligence--compact .fluid-capacity-hour__intelligence-head {
        padding: 0;
      }

      .fluid-capacity-hour__intelligence--compact .fluid-capacity-hour__intelligence-label,
      .fluid-capacity-hour__intelligence--compact .fluid-capacity-hour__intelligence-follow {
        padding: 1px 3px;
      }

      .fluid-capacity-hour__intelligence--compact .fluid-capacity-hour__intelligence-label {
        font-size: 7px;
      }

      .fluid-capacity-hour__intelligence--compact .fluid-capacity-hour__intelligence-follow {
        font-size: 6px;
        max-width: 40%;
      }
    }
  `],
})
export class FluidCapacityHourComponent implements OnChanges {
  private static nextGradientScopeId = 0;

  @Input() public hourLabel = '14:';
  @Input() public slots: FluidCapacityHourSlot[] = [];
  @Input() public carryState: FluidCapacityCarryState | null = null;
  @Input() public minHeight = 64;
  @Input() public splitMode: FluidCapacitySplitMode = 'adaptive';
  @Input() public geometryMode: FluidCapacityGeometryMode = 'diagonal';
  @Input() public renderVariant: FluidCapacityRenderVariant = 'standard';

  @Output() public readonly carryStateChange = new EventEmitter<FluidCapacityCarryState>();

  protected renderedSlots: FluidCapacityHourSegment[] = [];
  protected readonly colorFlowWidth = COLOR_FLOW_WIDTH_PERCENT;

  private readonly gradientScopeId = FluidCapacityHourComponent.nextGradientScopeId++;
  private lastCarryKey = '';

  public ngOnChanges(): void {
    this.renderedSlots = this.slots
      .map((slot) => this.toSegment(slot))
      .filter((slot): slot is FluidCapacityHourSegment => slot !== null)
      .sort((left, right) => left.clippedStartMinute - right.clippedStartMinute);
    this.applyRibbonContinuity(this.renderedSlots);

    this.emitCarryState();
  }

  protected slotTitle(slot: FluidCapacityHourSlot): string {
    const segment = slot as FluidCapacityHourSegment;
    return [
      `${slot.startLabel}-${slot.endLabel}`,
      `Devices ${slot.deviceAvailable}/${slot.deviceTotal}`,
      `Specialists ${slot.specialistAvailable}/${slot.specialistTotal}`,
      Number.isFinite(segment.splitLeftPercent) && Number.isFinite(segment.splitRightPercent)
        ? `Split ${segment.splitLeftPercent}% -> ${segment.splitRightPercent}%`
        : '',
      slot.status ? `Status ${slot.status}` : '',
      this.usesBusinessStateSemantics()
        ? `Business success ${this.roundPercent(segment.businessSuccessRatio * 100)}%`
        : `Throughput ${segment.throughputAvailable}/${segment.throughputTotal}`,
      segment.bottleneckKind !== 'none' ? `Bottleneck ${segment.bottleneckKind}` : '',
      `State ${segment.operationalState}`,
      segment.intelligenceLabel ? `Signal ${segment.intelligenceLabel}` : '',
      segment.intelligenceFollowUp ? `Follow-up ${segment.intelligenceFollowUp}` : '',
    ].filter(Boolean).join(' | ');
  }

  private toSegment(slot: FluidCapacityHourSlot): FluidCapacityHourSegment | null {
    const startMinute = this.clampMinute(slot.startMinute);
    const rawEndMinute = slot.startMinute + Math.max(1, slot.durationMinutes);
    const endMinute = this.clampMinute(rawEndMinute);
    if (endMinute <= startMinute) {
      return null;
    }

    const split = this.geometryMode === 'level'
      ? this.resolveLevelSplit(slot)
      : this.resolveSplit(slot);
    const physicalSplit = {
      left: this.toPhysicalSplitPercent(split.left),
      right: this.toPhysicalSplitPercent(split.right),
    };
    const isFullyConsumedBusinessSuccess = this.isFullyConsumedBusinessSuccess(slot);
    const deviceColors = isFullyConsumedBusinessSuccess
      ? this.resolveBusinessStateResourceColors(1, 'device')
      : this.resolveLoadColors(slot.deviceAvailable, slot.deviceTotal, 'device');
    const specialistColors = isFullyConsumedBusinessSuccess
      ? this.resolveBusinessStateResourceColors(1, 'specialist')
      : this.resolveLoadColors(slot.specialistAvailable, slot.specialistTotal, 'specialist');
    const bottleneckKind = this.resolveBottleneckKind(slot);
    const throughputTotal = Math.min(
      Math.max(0, slot.deviceTotal),
      Math.max(0, slot.specialistTotal),
    );
    const throughputAvailable = Math.min(
      Math.max(0, slot.deviceAvailable),
      Math.max(0, slot.specialistAvailable),
    );
    const throughputRatio = this.resolveEffectiveThroughputRatio(
      slot,
      throughputAvailable,
      throughputTotal,
    );
    const pressureRatio = this.resolvePressureRatio(slot);
    const intelligence = this.resolveSlotIntelligence(slot);
    const businessSuccessRatio = isFullyConsumedBusinessSuccess ? 1 : this.resolveBusinessSuccessRatio(slot);
    const isNearBusinessSuccess = this.isNearBusinessSuccess(slot, bottleneckKind);
    const primaryRatio = this.usesBusinessStateSemantics() ? businessSuccessRatio : throughputRatio;
    const primaryColor = this.usesBusinessStateSemantics()
      ? this.resolveBusinessSuccessColor(businessSuccessRatio)
      : this.resolveThroughputColor(throughputRatio);

    const segment: FluidCapacityHourSegment = {
      ...slot,
      clippedStartMinute: startMinute,
      clippedEndMinute: endMinute,
      startMinuteLabel: this.minuteLabelFromTime(slot.startLabel),
      endMinuteLabel: this.minuteLabelFromTime(slot.endLabel),
      resourceSummaryLabel: `D ${slot.deviceAvailable}/${slot.deviceTotal} S ${slot.specialistAvailable}/${slot.specialistTotal}`,
      leftPercent: (startMinute / 60) * 100,
      widthPercent: ((endMinute - startMinute) / 60) * 100,
      splitLeftPercent: physicalSplit.left,
      splitRightPercent: physicalSplit.right,
      splitLeftCss: `${physicalSplit.left}%`,
      splitRightCss: `${physicalSplit.right}%`,
      splitDataLeftPercent: physicalSplit.left,
      splitDataRightPercent: physicalSplit.right,
      deviceColorStart: deviceColors.start,
      deviceColorEnd: deviceColors.end,
      deviceFlowInletColor: deviceColors.start,
      specialistColorStart: specialistColors.start,
      specialistColorEnd: specialistColors.end,
      specialistFlowInletColor: specialistColors.start,
      continuesFromPrevious: false,
      continuesToNext: false,
      hasFlowCarry: false,
      deviceGradientId: `fluid-${this.gradientScopeId}-device-${this.safeDomId(slot.id)}`,
      deviceFlowGradientId: `fluid-${this.gradientScopeId}-device-flow-${this.safeDomId(slot.id)}`,
      specialistGradientId: `fluid-${this.gradientScopeId}-specialist-${this.safeDomId(slot.id)}`,
      specialistFlowGradientId: `fluid-${this.gradientScopeId}-specialist-flow-${this.safeDomId(slot.id)}`,
      devicePath: '',
      deviceFlowPath: '',
      specialistPath: '',
      specialistFlowPath: '',
      boundaryPath: '',
      deviceTargetBoundaryPath: '',
      specialistTargetBoundaryPath: '',
      bottleneckKind,
      operationalState: this.resolveOperationalState(slot),
      throughputAvailable,
      throughputTotal,
      throughputWidthCss: `${this.roundPercent(primaryRatio * 100)}%`,
      throughputOpacityCss: `${this.resolveThroughputOpacity(primaryRatio)}`,
      throughputColorCss: primaryColor,
      pressureOpacityCss: `${this.roundPercent(0.18 + pressureRatio * 0.58)}`,
      flowOpacityCss: `${this.resolveFlowOpacity(pressureRatio)}`,
      businessSuccessRatio,
      showThroughput: this.shouldShowThroughput(),
      showPressureOverlay: this.shouldShowPressureOverlay(),
      showStateLayer: this.shouldShowStateLayer(),
      isDeadThroughput: !isFullyConsumedBusinessSuccess && (this.usesBusinessStateSemantics() ? businessSuccessRatio <= 0.05 : throughputRatio <= 0.05),
      isBusinessSuccess: isFullyConsumedBusinessSuccess || (this.usesBusinessStateSemantics() && businessSuccessRatio >= 0.96 && bottleneckKind === 'none'),
      isNearBusinessSuccess,
      railState: this.resolveRailState(slot, businessSuccessRatio, throughputRatio),
      showCapacityIntelligence: this.usesCapacityIntelligenceVariant(),
      intelligenceSignal: intelligence.signal,
      intelligenceStrength: this.resolveIntelligenceStrength(slot, intelligence.signal, businessSuccessRatio),
      repeatsIntelligenceSignal: false,
      intelligenceLabel: intelligence.label,
      intelligenceFollowUp: intelligence.followUp,
      deviceRequiredTimeCss: `${intelligence.deviceRequiredTimePercent}%`,
      specialistOnlyTimeCss: `${100 - intelligence.deviceRequiredTimePercent}%`,
      specialistIdleCss: `${intelligence.specialistIdlePercent}%`,
      deviceIdleCss: `${intelligence.deviceIdlePercent}%`,
      preferenceSkewCss: `${Math.min(100, intelligence.preferenceSkewPercent ?? 0)}%`,
    };
    this.applySplitPaths(segment);
    return segment;
  }

  private applyRibbonContinuity(slots: FluidCapacityHourSegment[]): void {
    let carry: FluidCapacityVisualCarry | null = null;
    let previous: FluidCapacityHourSegment | null = null;

    for (const slot of slots) {
      const isContiguous = previous !== null && previous.clippedEndMinute === slot.clippedStartMinute;
      if (!isContiguous) {
        carry = null;
      }

      if (carry !== null) {
        slot.continuesFromPrevious = true;
        slot.splitLeftPercent = carry.splitExitPercent;
        slot.splitLeftCss = `${carry.splitExitPercent}%`;
        slot.deviceFlowInletColor = carry.deviceExitColor;
        slot.specialistFlowInletColor = carry.specialistExitColor;
        slot.hasFlowCarry = true;
        slot.flowOpacityCss = `${this.resolveFlowOpacity(carry.pressureExitRatio)}`;
        if (this.resolvePressureRatio(slot) + 0.25 < carry.pressureExitRatio) {
          slot.operationalState = 'recovery';
        }
      }

      this.applySplitPaths(slot);

      if (previous !== null && isContiguous) {
        previous.continuesToNext = true;
        if (
          this.renderVariant === 'capacity-intelligence-maturity'
          && previous.intelligenceSignal !== 'none'
          && previous.intelligenceSignal === slot.intelligenceSignal
        ) {
          previous.repeatsIntelligenceSignal = true;
          slot.repeatsIntelligenceSignal = true;
        }
      }

      carry = this.toVisualCarry(slot);
      previous = slot;
    }
  }

  private toVisualCarry(slot: FluidCapacityHourSegment): FluidCapacityVisualCarry {
    return {
      deviceExitColor: slot.deviceColorEnd,
      specialistExitColor: slot.specialistColorEnd,
      splitExitPercent: slot.splitDataRightPercent,
      pressureExitRatio: this.resolvePressureRatio(slot),
    };
  }

  private applySplitPaths(slot: FluidCapacityHourSegment): void {
    const split = this.buildSplitPath(
      slot.splitLeftPercent,
      slot.splitRightPercent,
      slot.splitDataLeftPercent,
      slot.splitDataRightPercent,
    );
    slot.boundaryPath = split.forward;
    slot.deviceTargetBoundaryPath = this.boundaryBandPath(
      slot.splitLeftPercent,
      slot.splitRightPercent,
      slot.splitDataLeftPercent,
      slot.splitDataRightPercent,
      -10,
      0,
    );
    slot.specialistTargetBoundaryPath = this.boundaryBandPath(
      slot.splitLeftPercent,
      slot.splitRightPercent,
      slot.splitDataLeftPercent,
      slot.splitDataRightPercent,
      0,
      10,
    );
    slot.devicePath = `M 0 0 H 100 V ${slot.splitRightPercent} ${split.reverse} Z`;
    slot.specialistPath = `${split.forward} L 100 100 H 0 Z`;
    slot.deviceFlowPath = `M 0 0 H ${COLOR_FLOW_WIDTH_PERCENT} V ${slot.splitLeftPercent} H 0 Z`;
    slot.specialistFlowPath = `M 0 ${slot.splitLeftPercent} H ${COLOR_FLOW_WIDTH_PERCENT} V 100 H 0 Z`;
  }

  private buildSplitPath(
    leftPortY: number,
    rightPortY: number,
    dataLeftY: number,
    dataRightY: number,
  ): { forward: string; reverse: string; startY: number } {
    const startX = SPLIT_RACCORD_WIDTH_PERCENT;
    const endX = 100 - SPLIT_RACCORD_WIDTH_PERCENT;
    const startY = this.interpolate(dataLeftY, dataRightY, SPLIT_RACCORD_WIDTH_PERCENT / 100);
    const endY = this.interpolate(dataLeftY, dataRightY, endX / 100);
    const startControlX = this.roundPercent(SPLIT_RACCORD_WIDTH_PERCENT / 3);
    const startControlEndX = this.roundPercent((SPLIT_RACCORD_WIDTH_PERCENT / 3) * 2);
    const endControlStartX = this.roundPercent(100 - (SPLIT_RACCORD_WIDTH_PERCENT / 3) * 2);
    const endControlX = this.roundPercent(100 - SPLIT_RACCORD_WIDTH_PERCENT / 3);

    return {
      forward: [
        `M 0 ${leftPortY}`,
        `C ${startControlX} ${leftPortY}, ${startControlEndX} ${startY}, ${startX} ${startY}`,
        `L ${endX} ${endY}`,
        `C ${endControlStartX} ${endY}, ${endControlX} ${rightPortY}, 100 ${rightPortY}`,
      ].join(' '),
      reverse: [
        `C ${endControlX} ${rightPortY}, ${endControlStartX} ${endY}, ${endX} ${endY}`,
        `L ${startX} ${startY}`,
        `C ${startControlEndX} ${startY}, ${startControlX} ${leftPortY}, 0 ${leftPortY}`,
      ].join(' '),
      startY,
    };
  }

  private boundaryBandPath(
    leftPortY: number,
    rightPortY: number,
    dataLeftY: number,
    dataRightY: number,
    topOffset: number,
    bottomOffset: number,
  ): string {
    const topLeftPortY = this.offsetPercent(leftPortY, topOffset);
    const topRightPortY = this.offsetPercent(rightPortY, topOffset);
    const topDataLeftY = this.offsetPercent(dataLeftY, topOffset);
    const topDataRightY = this.offsetPercent(dataRightY, topOffset);
    const bottomLeftPortY = this.offsetPercent(leftPortY, bottomOffset);
    const bottomRightPortY = this.offsetPercent(rightPortY, bottomOffset);
    const bottomDataLeftY = this.offsetPercent(dataLeftY, bottomOffset);
    const bottomDataRightY = this.offsetPercent(dataRightY, bottomOffset);
    const startX = SPLIT_RACCORD_WIDTH_PERCENT;
    const endX = 100 - SPLIT_RACCORD_WIDTH_PERCENT;
    const topStartY = this.interpolate(topDataLeftY, topDataRightY, SPLIT_RACCORD_WIDTH_PERCENT / 100);
    const topEndY = this.interpolate(topDataLeftY, topDataRightY, endX / 100);
    const bottomStartY = this.interpolate(bottomDataLeftY, bottomDataRightY, SPLIT_RACCORD_WIDTH_PERCENT / 100);
    const bottomEndY = this.interpolate(bottomDataLeftY, bottomDataRightY, endX / 100);
    const startControlX = this.roundPercent(SPLIT_RACCORD_WIDTH_PERCENT / 3);
    const startControlEndX = this.roundPercent((SPLIT_RACCORD_WIDTH_PERCENT / 3) * 2);
    const endControlStartX = this.roundPercent(100 - (SPLIT_RACCORD_WIDTH_PERCENT / 3) * 2);
    const endControlX = this.roundPercent(100 - SPLIT_RACCORD_WIDTH_PERCENT / 3);

    return [
      `M -2 ${topLeftPortY}`,
      `L 0 ${topLeftPortY}`,
      `C ${startControlX} ${topLeftPortY}, ${startControlEndX} ${topStartY}, ${startX} ${topStartY}`,
      `L ${endX} ${topEndY}`,
      `C ${endControlStartX} ${topEndY}, ${endControlX} ${topRightPortY}, 100 ${topRightPortY}`,
      `L 102 ${topRightPortY}`,
      `L 102 ${bottomRightPortY}`,
      `L 100 ${bottomRightPortY}`,
      `C ${endControlX} ${bottomRightPortY}, ${endControlStartX} ${bottomEndY}, ${endX} ${bottomEndY}`,
      `L ${startX} ${bottomStartY}`,
      `C ${startControlEndX} ${bottomStartY}, ${startControlX} ${bottomLeftPortY}, 0 ${bottomLeftPortY}`,
      `L -2 ${bottomLeftPortY}`,
      'Z',
    ].join(' ');
  }

  private offsetPercent(value: number, offset: number): number {
    return Math.max(0, Math.min(100, this.roundPercent(value + offset)));
  }

  private interpolate(from: number, to: number, amount: number): number {
    return this.roundPercent(from + (to - from) * amount);
  }

  private toPhysicalSplitPercent(logicalPercent: number): number {
    const logicalValue = Math.max(0, Math.min(100, logicalPercent));
    const availableRange = 100 - SPLIT_VERTICAL_INSET_PERCENT * 2;
    return this.roundPercent(SPLIT_VERTICAL_INSET_PERCENT + (logicalValue / 100) * availableRange);
  }

  private resolveSplit(slot: FluidCapacityHourSlot): { left: number; right: number } {
    if (this.splitMode === 'fixed') {
      return { left: 0, right: 100 };
    }

    const specialistRatio = this.availabilityRatio(slot.specialistAvailable, slot.specialistTotal);
    const deviceRatio = this.availabilityRatio(slot.deviceAvailable, slot.deviceTotal);
    const resourceDelta = deviceRatio - specialistRatio;
    const maxShift = this.renderVariant === 'soft-ribbon' ? 42 : 72;
    const shift = Math.min(maxShift, Math.abs(resourceDelta) * maxShift);

    if (resourceDelta > 0) {
      const bottleneckBand = this.resolveBottleneckBand(specialistRatio);
      return {
        left: this.roundPercent(Math.min(100 - bottleneckBand, shift)),
        right: this.roundPercent(100 - bottleneckBand),
      };
    }

    if (resourceDelta < 0) {
      const bottleneckBand = this.resolveBottleneckBand(deviceRatio);
      return {
        left: this.roundPercent(bottleneckBand),
        right: this.roundPercent(Math.max(bottleneckBand, 100 - shift)),
      };
    }

    return { left: 0, right: 100 };
  }

  private resolveLevelSplit(slot: FluidCapacityHourSlot): { left: number; right: number } {
    if (this.splitMode === 'fixed') {
      return { left: 50, right: 50 };
    }

    const specialistRatio = this.availabilityRatio(slot.specialistAvailable, slot.specialistTotal);
    const deviceRatio = this.availabilityRatio(slot.deviceAvailable, slot.deviceTotal);
    const resourceDelta = deviceRatio - specialistRatio;
    const maxShift = this.usesCapacityIntelligenceVariant() ? 72 : 36;
    const bottleneckInset = this.usesCapacityIntelligenceVariant() ? 8 : 14;
    const shiftedLevel = 50 + resourceDelta * maxShift;

    return {
      left: this.roundPercent(Math.max(bottleneckInset, Math.min(100 - bottleneckInset, shiftedLevel))),
      right: this.roundPercent(Math.max(bottleneckInset, Math.min(100 - bottleneckInset, shiftedLevel))),
    };
  }

  private availabilityRatio(available: number, total: number): number {
    if (!Number.isFinite(available) || !Number.isFinite(total) || total <= 0) {
      return 0;
    }

    return Math.max(0, Math.min(1, available / total));
  }

  private resolveBottleneckBand(ratio: number): number {
    if (ratio <= 0.05) {
      return 16;
    }

    if (ratio <= 0.2) {
      return 12;
    }

    if (ratio <= 0.35) {
      return 8;
    }

    return 0;
  }

  private resolveLoadColors(
    available: number,
    total: number,
    resource: FluidCapacityResource,
  ): { start: string; end: string } {
    const ratio = this.availabilityRatio(available, total);
    if (this.usesBusinessStateSemantics()) {
      return this.resolveBusinessStateResourceColors(ratio, resource);
    }

    const pressure = 1 - ratio;
    const healthy = LOAD_COLOR_SCALE[resource];
    const softPalette = this.shouldUseSoftPalette();

    if (pressure <= 0.65) {
      const progress = pressure / 0.65;
      return {
        start: this.rgbToCss(this.adjustPaletteColor(this.mixColor(healthy.healthyStart, LOAD_WARNING_START, progress), softPalette)),
        end: this.rgbToCss(this.adjustPaletteColor(this.mixColor(healthy.healthyEnd, LOAD_WARNING_END, progress), softPalette)),
      };
    }

    const progress = (pressure - 0.65) / 0.35;
    return {
      start: this.rgbToCss(this.adjustPaletteColor(this.mixColor(LOAD_WARNING_START, LOAD_DANGER_START, progress), softPalette)),
      end: this.rgbToCss(this.adjustPaletteColor(this.mixColor(LOAD_WARNING_END, LOAD_DANGER_END, progress), softPalette)),
    };
  }

  private resolveBusinessStateResourceColors(availabilityRatio: number, resource: FluidCapacityResource): { start: string; end: string } {
    const healthy = LOAD_COLOR_SCALE[resource];
    const available = Math.max(0, Math.min(1, availabilityRatio));
    const occupied = 1 - available;
    const base: RgbColor = [248, 250, 252];
    const activeStart = this.mixColor(base, healthy.healthyStart, occupied);
    const activeEnd = this.mixColor(base, healthy.healthyEnd, occupied);

    return {
      start: this.rgbToCss(activeStart),
      end: this.rgbToCss(activeEnd),
    };
  }

  private shouldShowThroughput(): boolean {
    return ['hybrid', 'throughput', 'throughput-band', 'pressure', 'state-layer', 'medium-pressure', 'operational-candidate', 'capacity-intelligence', 'capacity-intelligence-compact', 'capacity-intelligence-maturity'].includes(this.renderVariant);
  }

  private shouldShowPressureOverlay(): boolean {
    return ['pressure', 'bottleneck-scan', 'state-layer', 'operational-candidate', 'capacity-intelligence', 'capacity-intelligence-compact', 'capacity-intelligence-maturity'].includes(this.renderVariant);
  }

  private shouldShowStateLayer(): boolean {
    return this.renderVariant === 'state-layer';
  }

  private shouldUseSoftPalette(): boolean {
    return ['hybrid', 'soft-ribbon', 'soft-palette', 'state-layer', 'throughput-rail', 'continuity-rail', 'medium-pressure', 'operational-candidate', 'capacity-intelligence', 'capacity-intelligence-compact', 'capacity-intelligence-maturity'].includes(this.renderVariant);
  }

  private usesBusinessStateSemantics(): boolean {
    return this.usesCapacityIntelligenceVariant();
  }

  private usesCapacityIntelligenceVariant(): boolean {
    return ['capacity-intelligence', 'capacity-intelligence-compact', 'capacity-intelligence-maturity'].includes(this.renderVariant);
  }

  protected get showHourThroughputRail(): boolean {
    return this.renderVariant === 'throughput-rail';
  }

  protected get showContinuityRail(): boolean {
    return this.renderVariant === 'continuity-rail' || this.renderVariant === 'focus-flow';
  }

  private resolveThroughputColor(ratio: number): string {
    if (ratio <= 0.05) {
      return 'rgba(220, 38, 38, 0.78)';
    }

    if (ratio <= 0.35) {
      return 'rgba(217, 119, 6, 0.78)';
    }

    if (ratio <= 0.65) {
      return 'rgba(180, 83, 9, 0.62)';
    }

    return 'rgba(15, 23, 42, 0.5)';
  }

  private resolveBusinessSuccessColor(ratio: number): string {
    if (ratio >= 0.96) {
      return 'rgba(22, 163, 74, 0.72)';
    }

    if (ratio >= 0.68) {
      return 'rgba(21, 128, 61, 0.58)';
    }

    if (ratio >= 0.42) {
      return 'rgba(217, 119, 6, 0.62)';
    }

    return 'rgba(220, 38, 38, 0.72)';
  }

  private resolveRailState(slot: FluidCapacityHourSlot, businessSuccessRatio: number, throughputRatio: number): FluidCapacityRailState {
    if (this.renderVariant !== 'capacity-intelligence-maturity') {
      return businessSuccessRatio >= 0.82 ? 'healthy' : businessSuccessRatio >= 0.5 ? 'constrained' : 'fragile';
    }

    const signal = slot.intelligence?.signal ?? this.resolveDefaultIntelligenceSignal(slot);
    if (businessSuccessRatio <= 0.05) {
      return 'dead';
    }

    if (signal === 'preference-skew' || signal === 'mix-waste') {
      return 'fragile';
    }

    if (signal === 'healthy' && businessSuccessRatio >= 0.48 && throughputRatio <= 0.3) {
      return 'recovery';
    }

    if (businessSuccessRatio >= 0.82) {
      return 'healthy';
    }

    if (businessSuccessRatio >= 0.5) {
      return 'constrained';
    }

    return 'fragile';
  }

  private resolveIntelligenceStrength(
    slot: FluidCapacityHourSlot,
    signal: FluidCapacitySlotIntelligence['signal'],
    businessSuccessRatio: number,
  ): FluidCapacitySignalStrength {
    if (this.renderVariant !== 'capacity-intelligence-maturity') {
      return 'strong';
    }

    if (signal === 'mix-waste' || signal === 'preference-skew') {
      return 'weak';
    }

    if (
      signal === 'hire-specialists'
      || signal === 'add-devices'
      || (signal === 'expand' && businessSuccessRatio >= 0.96)
    ) {
      return 'strong';
    }

    return slot.intelligence ? 'strong' : 'weak';
  }

  private resolveBusinessSuccessRatio(slot: FluidCapacityHourSlot): number {
    if (typeof slot.intelligence?.businessSuccessScore === 'number') {
      return Math.max(0, Math.min(1, slot.intelligence.businessSuccessScore));
    }

    const specialistAvailability = this.availabilityRatio(slot.specialistAvailable, slot.specialistTotal);
    const deviceAvailability = this.availabilityRatio(slot.deviceAvailable, slot.deviceTotal);
    const specialistUsed = 1 - specialistAvailability;
    const deviceUsed = 1 - deviceAvailability;
    const specialistFullyUsed = specialistAvailability <= 0.05;
    const deviceFullyUsed = deviceAvailability <= 0.05;

    if (specialistFullyUsed && deviceFullyUsed) {
      return 1;
    }

    if (specialistFullyUsed || deviceFullyUsed) {
      return 0.5;
    }

    const utilization = (specialistUsed + deviceUsed) / 2;
    const mismatchPenalty = Math.abs(specialistUsed - deviceUsed) * 0.2;

    return Math.max(0, Math.min(1, utilization - mismatchPenalty));
  }

  private resolveEffectiveThroughputRatio(
    slot: FluidCapacityHourSlot,
    throughputAvailable: number,
    throughputTotal: number,
  ): number {
    if (typeof slot.intelligence?.effectiveThroughputScore === 'number') {
      return Math.max(0, Math.min(1, slot.intelligence.effectiveThroughputScore));
    }

    return this.availabilityRatio(throughputAvailable, throughputTotal);
  }

  private resolveThroughputOpacity(ratio: number): number {
    if (
      this.renderVariant === 'operational-candidate'
      || this.renderVariant === 'capacity-intelligence'
      || this.renderVariant === 'capacity-intelligence-compact'
      || this.renderVariant === 'capacity-intelligence-maturity'
    ) {
      if (ratio <= 0.05) {
        return 0.92;
      }

      return this.roundPercent(0.58 + ratio * 0.3);
    }

    if (this.renderVariant === 'throughput-band') {
      return this.roundPercent(0.48 + ratio * 0.36);
    }

    if (this.renderVariant === 'medium-pressure') {
      return this.roundPercent(0.38 + ratio * 0.28);
    }

    return this.roundPercent(0.52 + ratio * 0.34);
  }

  private resolveSlotIntelligence(slot: FluidCapacityHourSlot): ResolvedFluidCapacitySlotIntelligence {
    const deviceRequiredTimePercent = this.clampPercent(slot.intelligence?.deviceRequiredTimePercent ?? 50);
    const specialistIdlePercent = this.clampPercent(
      slot.intelligence?.specialistIdlePercent ?? (1 - this.availabilityRatio(slot.specialistAvailable, slot.specialistTotal)) * 100,
    );
    const deviceIdlePercent = this.clampPercent(
      slot.intelligence?.deviceIdlePercent ?? (1 - this.availabilityRatio(slot.deviceAvailable, slot.deviceTotal)) * 100,
    );
    const preferenceSkewPercent = this.clampPercent(slot.intelligence?.preferenceSkewPercent ?? 0);
    const signal = slot.intelligence?.signal ?? this.resolveDefaultIntelligenceSignal(slot);

    return {
      signal,
      label: slot.intelligence?.label ?? this.defaultIntelligenceLabel(signal),
      followUp: slot.intelligence?.followUp ?? this.defaultIntelligenceFollowUp(signal),
      deviceRequiredTimePercent,
      specialistIdlePercent,
      deviceIdlePercent,
      preferenceSkewPercent,
    };
  }

  private resolveDefaultIntelligenceSignal(slot: FluidCapacityHourSlot): FluidCapacitySlotIntelligence['signal'] {
    const specialistRatio = this.availabilityRatio(slot.specialistAvailable, slot.specialistTotal);
    const deviceRatio = this.availabilityRatio(slot.deviceAvailable, slot.deviceTotal);

    if (specialistRatio <= 0.05 && deviceRatio <= 0.05) {
      return 'expand';
    }

    if (specialistRatio <= 0.2 && deviceRatio >= 0.45) {
      return 'hire-specialists';
    }

    if (deviceRatio <= 0.2 && specialistRatio >= 0.45) {
      return 'add-devices';
    }

    return 'healthy';
  }

  private defaultIntelligenceLabel(signal: FluidCapacitySlotIntelligence['signal']): string {
    switch (signal) {
      case 'expand':
        return 'Expand';
      case 'hire-specialists':
        return 'Hire S';
      case 'add-devices':
        return 'Add D';
      case 'preference-skew':
        return 'Preference';
      case 'mix-waste':
        return 'Mix waste';
      case 'blocked':
        return 'Blocked';
      case 'healthy':
        return 'Healthy';
    }
  }

  private defaultIntelligenceFollowUp(signal: FluidCapacitySlotIntelligence['signal']): string {
    switch (signal) {
      case 'expand':
        return 'capacity';
      case 'hire-specialists':
        return 'staff';
      case 'add-devices':
        return 'devices';
      case 'preference-skew':
        return 'specialist';
      case 'mix-waste':
        return 'mix';
      case 'blocked':
        return 'capacity';
      case 'healthy':
        return 'ok';
    }
  }

  private resolveFlowOpacity(pressureRatio: number): number {
    if (this.renderVariant === 'hybrid') {
      return this.roundPercent(0.18 + pressureRatio * 0.22);
    }

    if (this.renderVariant === 'soft-ribbon' || this.renderVariant === 'soft-palette') {
      return this.roundPercent(0.24 + pressureRatio * 0.28);
    }

    return 0.55;
  }

  private resolveBottleneckKind(slot: FluidCapacityHourSlot): FluidCapacityBottleneck {
    if (this.isFullyConsumedBusinessSuccess(slot)) {
      return 'none';
    }

    if (this.usesCapacityIntelligenceVariant()) {
      if (slot.deviceAvailable <= 0 && slot.deviceTotal > 0) {
        return 'device';
      }

      if (slot.specialistAvailable <= 0 && slot.specialistTotal > 0) {
        return 'specialist';
      }
    }

    const specialistRatio = this.availabilityRatio(slot.specialistAvailable, slot.specialistTotal);
    const deviceRatio = this.availabilityRatio(slot.deviceAvailable, slot.deviceTotal);
    const specialistPressure = 1 - specialistRatio;
    const devicePressure = 1 - deviceRatio;
    const nearEqualPressure = Math.abs(specialistPressure - devicePressure) <= 0.08;
    const highPressure = specialistPressure >= 0.55 && devicePressure >= 0.55;

    if (highPressure || (nearEqualPressure && specialistPressure >= 0.35 && devicePressure >= 0.35)) {
      return 'both';
    }

    if (specialistPressure < 0.2 && devicePressure < 0.2) {
      return 'none';
    }

    return devicePressure > specialistPressure ? 'device' : 'specialist';
  }

  private isFullyConsumedBusinessSuccess(slot: FluidCapacityHourSlot): boolean {
    return this.usesCapacityIntelligenceVariant()
      && slot.deviceTotal > 0
      && slot.specialistTotal > 0
      && slot.deviceAvailable <= 0
      && slot.specialistAvailable <= 0;
  }

  private isNearBusinessSuccess(slot: FluidCapacityHourSlot, bottleneckKind: FluidCapacityBottleneck): boolean {
    return this.usesCapacityIntelligenceVariant()
      && bottleneckKind !== 'none'
      && !this.isFullyConsumedBusinessSuccess(slot)
      && slot.deviceTotal > 0
      && slot.specialistTotal > 0
      && slot.deviceAvailable <= 1
      && slot.specialistAvailable <= 1;
  }

  private resolveOperationalState(slot: FluidCapacityHourSlot): FluidCapacityOperationalState {
    const specialistRatio = this.availabilityRatio(slot.specialistAvailable, slot.specialistTotal);
    const deviceRatio = this.availabilityRatio(slot.deviceAvailable, slot.deviceTotal);
    const throughputRatio = this.availabilityRatio(
      Math.min(Math.max(0, slot.deviceAvailable), Math.max(0, slot.specialistAvailable)),
      Math.min(Math.max(0, slot.deviceTotal), Math.max(0, slot.specialistTotal)),
    );
    const resourceGap = Math.abs(specialistRatio - deviceRatio);
    const pressureRatio = 1 - Math.min(specialistRatio, deviceRatio);

    if (throughputRatio <= 0.05) {
      return 'overloaded';
    }

    if (pressureRatio >= 0.62 || resourceGap >= 0.55) {
      return 'unstable';
    }

    if (pressureRatio >= 0.42) {
      return 'predicted-saturation';
    }

    return 'stable';
  }

  private resolvePressureRatio(slot: Pick<FluidCapacityHourSlot, 'specialistAvailable' | 'specialistTotal' | 'deviceAvailable' | 'deviceTotal'>): number {
    const specialistRatio = this.availabilityRatio(slot.specialistAvailable, slot.specialistTotal);
    const deviceRatio = this.availabilityRatio(slot.deviceAvailable, slot.deviceTotal);
    return Math.max(0, Math.min(1, 1 - Math.min(specialistRatio, deviceRatio)));
  }

  private adjustPaletteColor(color: RgbColor, softPalette: boolean): RgbColor {
    if (!softPalette) {
      return color;
    }

    return this.mixColor(color, [248, 250, 252], 0.28);
  }

  private mixColor(from: RgbColor, to: RgbColor, amount: number): RgbColor {
    const clampedAmount = Math.max(0, Math.min(1, amount));
    return [
      Math.round(from[0] + (to[0] - from[0]) * clampedAmount),
      Math.round(from[1] + (to[1] - from[1]) * clampedAmount),
      Math.round(from[2] + (to[2] - from[2]) * clampedAmount),
    ];
  }

  private rgbToCss(color: RgbColor): string {
    return `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
  }

  private safeDomId(value: string): string {
    return value.replace(/[^a-zA-Z0-9_-]/g, '-');
  }

  private minuteLabelFromTime(value: string): string {
    const match = value.match(/:(\d{2})/);
    if (match) {
      return match[1];
    }

    return value.slice(-2);
  }

  private roundPercent(value: number): number {
    return Math.round(value * 10) / 10;
  }

  private emitCarryState(): void {
    const overflowingSlot = this.slots.find((slot) => slot.startMinute + slot.durationMinutes > 60);
    const nextCarry: FluidCapacityCarryState = overflowingSlot
      ? {
          overflowSlotId: overflowingSlot.id,
          overflowMinutes: overflowingSlot.startMinute + overflowingSlot.durationMinutes - 60,
        }
      : {
          overflowSlotId: null,
          overflowMinutes: 0,
        };

    const nextKey = `${nextCarry.overflowSlotId ?? ''}|${nextCarry.overflowMinutes}`;
    if (nextKey === this.lastCarryKey) {
      return;
    }

    this.lastCarryKey = nextKey;
    this.carryStateChange.emit(nextCarry);
  }

  private clampMinute(value: number): number {
    if (!Number.isFinite(value)) {
      return 0;
    }

    return Math.max(0, Math.min(60, value));
  }

  private clampPercent(value: number): number {
    if (!Number.isFinite(value)) {
      return 0;
    }

    return Math.max(0, Math.min(100, this.roundPercent(value)));
  }
}
